import React, { useState, useEffect } from "react";
import { useShop } from "providers/ShopProvider";
import { Banner, Heading, TextContainer } from "@shopify/polaris";
import { getSubscriptionStatus, getStripePortalUrl } from "services/wpApi";

/**
 * Displays a banner when a production subscription has issues.
 *
 * This component only appears for shops with a production license key
 * (not in sandbox mode). It fetches the current Stripe subscription status
 * and shows warnings for:
 * - Canceled subscriptions
 * - Past due payments
 * - Unpaid invoices
 * - Subscriptions set to cancel at period end
 *
 * Note: License keys are only issued after successful payment, so statuses
 * like "incomplete" are not possible for shops with license keys.
 */
export default function SubscriptionStatusBanner() {
  const { shop } = useShop();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);

  const openStripePortal = async () => {
    setLoadingPortal(true);
    try {
      const { data } = await getStripePortalUrl();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setLoadingPortal(false);
      }
    } catch (err) {
      console.error("Failed to open billing portal:", err);
      setLoadingPortal(false);
    }
  };

  useEffect(() => {
    // Only check subscription status for production shops with a license key
    if (shop?.sandboxMode || !shop?.licenseKey) {
      setLoading(false);
      return;
    }

    const fetchSubscriptionStatus = async () => {
      try {
        const { data } = await getSubscriptionStatus();
        setSubscriptionStatus(data);
      } catch (err) {
        console.error("Error fetching subscription status:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [shop?.sandboxMode, shop?.licenseKey]);

  // Don't render anything if:
  // - Still loading
  // - In sandbox mode
  // - No license key (not a production shop)
  if (loading || shop?.sandboxMode || !shop?.licenseKey) {
    return null;
  }

  // Don't show if there's no subscription status or it's active (without pending cancellation)
  if (!subscriptionStatus || (subscriptionStatus.status === "active" && !subscriptionStatus.cancelAtPeriodEnd)) {
    return null;
  }

  // Handle subscription set to cancel at period end (but still active)
  if (subscriptionStatus.status === "active" && subscriptionStatus.cancelAtPeriodEnd) {
    return (
      <div className="ri-my-4">
        <Banner
          status="warning"
          action={{
            content: loadingPortal ? "Opening..." : "Manage Subscription",
            onAction: openStripePortal,
            disabled: loadingPortal,
          }}
        >
          <Heading>Subscription Ending Soon</Heading>
          <TextContainer>
            <p className="ri-mb-2">
              Your subscription is set to cancel at the end of the current billing period
              {subscriptionStatus.currentPeriodEnd && (
                <> on {new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString()}</>
              )}
              . To continue using Real ID without interruption, please renew your subscription.
            </p>
          </TextContainer>
        </Banner>
      </div>
    );
  }

  // Determine banner content based on subscription status
  const getBannerContent = () => {
    switch (subscriptionStatus.status) {
      case "canceled":
        return {
          title: "Subscription Canceled",
          status: "critical",
          message: "Your subscription has been canceled. ID verification features are no longer active. Please subscribe again to continue using Real ID.",
          action: {
            content: "Subscribe Now",
            url: "https://dashboard.getverdict.com",
          },
        };

      case "past_due":
        return {
          title: "Payment Past Due",
          status: "warning",
          message: subscriptionStatus.latestInvoice?.hostedInvoiceUrl
            ? "Your subscription payment is past due. Please update your payment method or pay the outstanding invoice to avoid service interruption."
            : "Your subscription payment is past due. Please update your payment method to avoid service interruption.",
          action: subscriptionStatus.latestInvoice?.hostedInvoiceUrl
            ? {
                content: "Pay Invoice",
                url: subscriptionStatus.latestInvoice.hostedInvoiceUrl,
              }
            : {
                content: loadingPortal ? "Opening..." : "Update Payment Method",
                onAction: openStripePortal,
                disabled: loadingPortal,
              },
        };

      case "unpaid":
        return {
          title: "Unpaid Invoice",
          status: "critical",
          message: "Your subscription has unpaid invoices. Please settle the outstanding balance to continue using Real ID.",
          action: subscriptionStatus.latestInvoice?.hostedInvoiceUrl
            ? {
                content: "Pay Invoice",
                url: subscriptionStatus.latestInvoice.hostedInvoiceUrl,
              }
            : {
                content: loadingPortal ? "Opening..." : "View Billing",
                onAction: openStripePortal,
                disabled: loadingPortal,
              },
        };

      case "paused":
        return {
          title: "Subscription Paused",
          status: "info",
          message: "Your subscription is currently paused. ID verification features may be limited.",
          action: {
            content: loadingPortal ? "Opening..." : "Resume Subscription",
            onAction: openStripePortal,
            disabled: loadingPortal,
          },
        };

      default:
        return null;
    }
  };

  const bannerContent = getBannerContent();

  if (!bannerContent) {
    return null;
  }

  return (
    <div className="ri-my-4">
      <Banner
        status={bannerContent.status}
        action={bannerContent.action}
        secondaryAction={{
          content: "Contact Support",
          url: "https://getverdict.com/contact",
        }}
      >
        <Heading>{bannerContent.title}</Heading>
        <TextContainer>
          <p className="ri-mb-2">{bannerContent.message}</p>
          {subscriptionStatus.latestInvoice && (
            <p className="ri-text-sm ri-text-gray-600">
              Invoice amount: ${(subscriptionStatus.latestInvoice.amountDue / 100).toFixed(2)} {subscriptionStatus.latestInvoice.currency?.toUpperCase()}
            </p>
          )}
        </TextContainer>
      </Banner>
    </div>
  );
}
